<template>
  <!-- 延期统计：延期判定/原因分布/多维度报表（按任务 delay_status 取数） -->
  <div class="delay-stat-list">
    <list-page>
      <search-condition @search="loadOverview" :show-search="true">
        <template #default>
          <el-select v-model="query.projectId" placeholder="全部项目" clearable filterable @change="loadOverview">
            <template #prefix>所属项目</template>
            <el-option v-for="p in projectOptions" :key="p.id" :label="p.projectName" :value="p.id"/>
          </el-select>
          <employee-select v-model="query.ownerId" placeholder="负责人（全部）" @change="loadOverview">
            <template #prefix>负责人</template>
          </employee-select>
        </template>
        <template #option>
          <page-button type="read" el-type="primary" @click="loadOverview">查询</page-button>
        </template>
      </search-condition>
      <list-table class="stat-body" v-loading="loading">
        <!-- 汇总卡：直接作为 list-page 的子元素，不放进表格容器 -->
        <div class="stat-grid">
          <div class="stat-card">
            <div class="label">⏰ 延期任务</div>
            <div class="value" style="color:#f56c6c">{{ stat.delayedCount || 0 }}<span class="unit">个</span></div>
            <div class="sub">其中延期中 {{ stat.ongoingDelayCount || 0 }} 个</div>
          </div>
          <div class="stat-card">
            <div class="label">📈 延期率</div>
            <div class="value" :style="{color: rateColor(stat.delayRate)}">{{ stat.delayRate || 0 }}<span class="unit">%</span></div>
            <div class="sub">已完结 {{ stat.finishedCount || 0 }} 个中延期完成 {{ stat.finishedDelayedCount || 0 }} 个</div>
          </div>
          <div class="stat-card">
            <div class="label">🗓 累计延期天数</div>
            <div class="value" style="color:#e6a23c">{{ stat.totalDelayDays || 0 }}<span class="unit">天</span></div>
            <div class="sub">全部延期任务合计</div>
          </div>
          <div class="stat-card">
            <div class="label">⚖ 平均延期</div>
            <div class="value">{{ fmtAvg(stat.avgDelayDays) }}</div>
            <div class="sub">单个任务平均延期时长</div>
          </div>
        </div>

        <div class="tip-bar">
          统计口径：以下数据只包含当前筛选范围内的任务。「延期率」= 延期完成的任务数 ÷ 已完结任务数，进行中的任务不计入分母；
          「延期中」任务的天数按当前日期逐日累计。
        </div>
        <!-- 延期总览（按迭代批次） -->
        <div class="chart-card">
          <div class="section-title">延期总览<span class="section-sub">按迭代批次</span></div>
          <yq-table :data="stat.sprintRows || []" height="auto" :empty-text="'暂无迭代批次'">
            <yq-table-column label="批次" prop="sprintName" min-width="180rem"/>
            <yq-table-column label="所属项目" prop="projectName" min-width="160rem"/>
            <yq-table-column label="期望完成" prop="expectDate" width="110rem">
              <template v-slot="{row}">{{ dateOnly(row.expectDate) }}</template>
            </yq-table-column>
            <yq-table-column label="任务数" prop="taskCount" width="80rem" align="center"/>
            <yq-table-column label="已完结" prop="finishedCount" width="80rem" align="center"/>
            <yq-table-column label="延期数" prop="delayedCount" width="80rem" align="center">
              <template v-slot="{row}"><b class="num neg">{{ row.delayedCount || 0 }}</b></template>
            </yq-table-column>
            <yq-table-column label="延期率" prop="delayRate" width="90rem" align="center">
              <template v-slot="{row}"><span class="num" :style="{color: rateColor(row.delayRate)}">{{ row.delayRate || 0 }}%</span></template>
            </yq-table-column>
            <yq-table-column label="累计延期天数" prop="delayDays" width="110rem" align="right">
              <template v-slot="{row}"><span class="num">{{ row.delayDays || 0 }} 天</span></template>
            </yq-table-column>
          </yq-table>
        </div>
        <!-- 人员延期榜 + 延期原因分布 -->
        <div class="grid-2">
          <div class="chart-card">
            <div class="section-title">人员延期榜<span class="section-sub">按延期任务数</span></div>
            <yq-table :data="stat.personRows || []" height="auto" :empty-text="'暂无延期任务'">
              <yq-table-column label="成员" prop="ownerName" min-width="140rem"/>
              <yq-table-column label="延期任务数" prop="delayedCount" width="110rem" align="center">
                <template v-slot="{row}"><b class="num neg">{{ row.delayedCount }}</b></template>
              </yq-table-column>
              <yq-table-column label="累计延期天数" prop="delayDays" width="130rem" align="right">
                <template v-slot="{row}"><span class="num">{{ row.delayDays }} 天</span></template>
              </yq-table-column>
            </yq-table>
          </div>
          <div class="chart-card">
            <div class="section-title">延期原因分布</div>
            <div v-if="!(stat.reasonRows || []).length" class="empty-block">暂无延期任务</div>
            <div v-else class="reason-list">
              <div v-for="r in stat.reasonRows" :key="r.category" class="reason-row">
                <span class="reason-label">{{ r.category }}</span>
                <div class="reason-bar-wrap">
                  <div class="reason-bar" :style="{width: reasonBarWidth(r.count) + '%'}"/>
                </div>
                <span class="reason-count">{{ r.count }}</span>
              </div>
            </div>
          </div>
        </div>
        <!-- 项目维度延期榜 + 登记规则说明 -->
        <div class="grid-2">
          <div class="chart-card">
            <div class="section-title">项目维度<span class="section-sub">延期最多</span></div>
            <yq-table :data="projectTop" height="auto" :empty-text="'暂无延期任务'">
              <yq-table-column label="项目" prop="projectName" min-width="200rem">
                <template v-slot="{row}">
                  <div class="cell-title">{{ row.projectName }}</div>
                  <div v-if="row.projectCode" class="muted code-text">{{ row.projectCode }}</div>
                </template>
              </yq-table-column>
              <yq-table-column label="延期任务数" prop="delayedCount" width="110rem" align="center">
                <template v-slot="{row}"><b class="num neg">{{ row.delayedCount }}</b></template>
              </yq-table-column>
              <yq-table-column label="累计延期天数" prop="delayDays" width="130rem" align="right">
                <template v-slot="{row}"><span class="num">{{ row.delayDays }} 天</span></template>
              </yq-table-column>
            </yq-table>
          </div>
          <div class="chart-card">
            <div class="section-title">延期登记规则说明</div>
            <div class="rule-block">
              · 已完成任务：实际完成时间 &gt; 计划完成时间，延期天数 = 实际完成 − 计划完成。<br>
              · 未完成任务：当前日期 &gt; 计划完成时间，视为「延期中」，天数逐日累计。<br>
              · <b>延期任务必须先登记原因才能标记完成</b>，保证统计数据完整可信。<br>
              · 延期率 = 延期完成的任务数 ÷ 已完结任务数 × 100%，用于量化团队交付准确度。
            </div>
          </div>
        </div>
        <!-- 延期明细 -->
        <div class="chart-card">
          <div class="section-title detail-title">
            <span>延期明细</span>
            <page-button el-type="success" plain size="small" @click="exportCsv">导出延期明细 CSV</page-button>
          </div>
          <yq-table :data="stat.detailTasks || []" height="auto" :empty-text="'当前没有延期任务'">
            <yq-table-column label="任务" prop="taskName" min-width="200rem" :show-overflow-tooltip="true">
              <template v-slot="{row}">
                <div class="cell-title">{{ row.taskName }}</div>
                <div v-if="row.taskCode" class="muted code-text">{{ row.taskCode }}</div>
              </template>
            </yq-table-column>
            <yq-table-column label="负责人" prop="ownerName" width="90rem">
              <template v-slot="{row}">{{ row.ownerName || '—' }}</template>
            </yq-table-column>
            <yq-table-column label="所属项目" prop="projectName" min-width="140rem">
              <template v-slot="{row}">{{ row.projectName || '—' }}</template>
            </yq-table-column>
            <yq-table-column label="计划完成" prop="planEnd" width="110rem">
              <template v-slot="{row}">{{ dateOnly(row.planEnd) }}</template>
            </yq-table-column>
            <yq-table-column label="实际完成" prop="actualEnd" width="110rem">
              <template v-slot="{row}">{{ row.taskStatus === TaskStatusCode.DONE ? dateOnly(row.actualEnd) : '未完成' }}</template>
            </yq-table-column>
            <yq-table-column label="延期天数" prop="delayDays" width="90rem" align="center">
              <template v-slot="{row}"><span class="num delay-text">{{ row.delayDays }} 天</span></template>
            </yq-table-column>
            <yq-table-column label="状态" prop="taskStatus" width="90rem" align="center">
              <template v-slot="{row}">
                <el-tag size="small" :type="taskStatusTag(row.taskStatus)">{{ taskStatusLabel(row.taskStatus) || '—' }}</el-tag>
              </template>
            </yq-table-column>
            <yq-table-column label="原因分类" prop="reasonCategory" width="130rem">
              <template v-slot="{row}">
                <el-tag v-if="row.reasonCategory && row.reasonCategory !== '未填写'" size="small" type="warning">
                  {{ row.reasonCategory }}
                </el-tag>
                <span v-else class="delay-text">未填写</span>
              </template>
            </yq-table-column>
            <yq-table-column label="原因说明" prop="reasonNote" min-width="180rem" :show-overflow-tooltip="true">
              <template v-slot="{row}">{{ row.reasonNote || '—' }}</template>
            </yq-table-column>
          </yq-table>
        </div>
      </list-table>
    </list-page>
  </div>
</template>

<script>
import EmployeeSelect from "@/components/h5/data/EmployeeSelect.vue";
import {useRoute, useRouter} from "vue-router";
import Util from "@/util/util";
import statisticsApi from "@/api/dz-project/project/statisticsApi";
import {TaskStatusCode, taskStatusLabel as taskStatusLabelOf, taskStatusTag as taskStatusTagOf} from "../manage/projectStatus";
import YqTable from "@/components/h5/table/YqTable.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";

export default {
  setup() {
    return {router: useRouter(), route: useRoute()};
  },
  name: "DelayStatList",
  components: {ListTable, YqTable, EmployeeSelect},
  data() {
    return {
      query: {projectId: "", ownerId: ""},
      projectOptions: [],
      stat: {},
      loading: false,
      TaskStatusCode,
    };
  },
  computed: {
    projectTop() {
      return (this.stat.projectRows || []).slice(0, 8);
    },
  },
  beforeRouteLeave(to, from, next) {
    Util.cachePageData(this, to, from);
    next();
  },
  created() {
    this.initOptions();
    this.loadOverview();
  },
  methods: {
    async initOptions() {
      const list = await statisticsApi.queryProjectOptions();
      this.projectOptions = list || [];
    },
    async loadOverview() {
      this.loading = true;
      try {
        const params = {};
        if (this.query.projectId) {
          params.projectId = this.query.projectId;
        }
        if (this.query.ownerId) {
          params.ownerId = this.query.ownerId;
        }
        const data = await statisticsApi.overview(params);
        this.stat = data || {};
      } finally {
        this.loading = false;
      }
    },
    dateOnly(v) {
      return v ? String(v).slice(0, 10) : "—";
    },
    fmtAvg(v) {
      if (v === null || v === undefined || v === "") {
        return "0";
      }
      return Number(v).toFixed(1) + " 天";
    },
    rateColor(r) {
      const n = Number(r) || 0;
      return n > 30 ? "#f56c6c" : n > 0 ? "#e6a23c" : "#67c23a";
    },
    reasonBarWidth(count) {
      const rows = this.stat.reasonRows || [];
      const max = rows.reduce((m, r) => Math.max(m, Number(r.count) || 0), 0);
      if (!max) {
        return 0;
      }
      return Math.round((Number(count) || 0) * 100 / max);
    },
    taskStatusLabel(status) {
      return taskStatusLabelOf(status);
    },
    taskStatusTag(status) {
      return taskStatusTagOf(status);
    },
    exportCsv() {
      const rows = this.stat.detailTasks || [];
      if (!rows.length) {
        this.$message.warning("当前没有延期明细可导出");
        return;
      }
      const header = ["任务", "任务编号", "负责人", "所属项目", "计划完成", "实际完成", "延期天数", "状态", "原因分类", "原因说明"];
      const escape = (v) => {
        const s = v === null || v === undefined ? "" : String(v);
        return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
      };
      const lines = [header.map(escape).join(",")];
      rows.forEach(r => {
        const done = r.taskStatus === TaskStatusCode.DONE;
        lines.push([
          r.taskName || "",
          r.taskCode || "",
          r.ownerName || "",
          r.projectName || "",
          this.dateOnly(r.planEnd),
          done ? this.dateOnly(r.actualEnd) : "未完成",
          r.delayDays || 0,
          r.delayState || "",
          r.reasonCategory || "",
          r.reasonNote || "",
        ].map(escape).join(","));
      });
      // 带 BOM，避免 Excel 打开中文乱码
      const blob = new Blob(["﻿" + lines.join("\r\n")], {type: "text/csv;charset=utf-8;"});
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "延期明细-" + this.todayCode() + ".csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    },
    todayCode() {
      const d = new Date();
      return d.getFullYear() + String(d.getMonth() + 1).padStart(2, "0") + String(d.getDate()).padStart(2, "0");
    },
  },
};
</script>

<style lang="scss" scoped>
.delay-stat-list {
  height: 100%;
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 14px;

  .stat-card {
    border: 1px solid var(--yq-border-color, #ebeef5);
    border-radius: 6px;
    padding: 12px 16px;
    background: #fff;

    .label {
      font-size: 12px;
      color: #909399;
    }

    .value {
      font-size: 24px;
      font-weight: 700;
      height: 34px;
      line-height: 34px;
      margin: 4px 0;

      &.money { font-size: 20px; }
      &.neg { color: #e5484d; }

      .unit {
        font-size: 14px;
        font-weight: 400;
        margin-left: 2px;
      }
    }

    .sub {
      font-size: 12px;
      color: #909399;
    }
  }
}

.tip-bar {
  font-size: 12px;
  color: #909399;
  line-height: 1.7;
  background: #fafbfc;
  border: 1px solid var(--yq-border-color, #ebeef5);
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 12px;
}

.grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.chart-card {
  border: 1px solid var(--yq-border-color, #ebeef5);
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 12px;
  background: #fff;

  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 10px;

    .section-sub {
      font-size: 12px;
      font-weight: 400;
      color: #909399;
      margin-left: 8px;
    }
  }
}

.num { font-variant-numeric: tabular-nums; }
.neg { color: #e5484d; }
.muted { color: #909399; }
.cell-title { color: #303133; font-weight: 500; }

.stat-body {
  .code-text {
    font-family: Consolas, Menlo, monospace;
    font-size: 11px;
  }

  .delay-text {
    color: #f56c6c;
    font-weight: 600;
  }

  /* 延期原因分布：纯 CSS 横条 */
  .reason-list {
    .reason-row {
      display: flex;
      align-items: center;
      margin-bottom: 6px;

      .reason-label {
        width: 150px;
        text-align: right;
        margin-right: 8px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .reason-bar-wrap {
        flex: 1;
        height: 14px;
        background: #f2f3f5;
        border-radius: 3px;
        overflow: hidden;

        .reason-bar {
          height: 100%;
          background: linear-gradient(90deg, #f7ba2a, #e6a23c);
          border-radius: 3px;
        }
      }

      .reason-count {
        width: 40px;
        text-align: right;
        font-weight: 600;
      }
    }
  }

  .empty-block {
    color: #909399;
    padding: 10px 0;
  }

  .rule-block {
    line-height: 1.9;
    color: #909399;
    font-size: 13px;
    background: #fafbfc;
    border: 1px dashed var(--yq-border-color, #ebeef5);
    border-radius: 6px;
    padding: 10px 12px;
  }

  .detail-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
}
</style>
